/**
 * The market-page indexation rule — the single copy, shared by the app and the
 * deploy-time sitemap generator.
 *
 * Two things decide whether a `/markets/...` event page exists for search:
 *
 *   1. the `robots` meta the page renders (`app/(main)/markets/[...slug]/seoMetadata.ts`,
 *      via the `utils/markets.ts` wrapper), and
 *   2. whether `sitemapper.js` lists its URL in `public/sitemap-markets.xml`.
 *
 * They have to answer the same question, and they used to answer it from two
 * hand-copied implementations under a "keep in sync" comment. They had already
 * drifted: the deploy-time copy was missing the determined-series exclusion below,
 * so pages the app rendered `noindex` were still advertised for crawling. This file
 * is why that can't happen again — it has **no dependencies of any kind**, so
 * `sitemapper.js` can load it under bare `node` (the deploy bot never installs this
 * repo's `node_modules`, and no build step runs ahead of it), while the app imports
 * it like any other module and takes its types from `marketPageIndexation.d.mts`.
 *
 * ESM with an `.mjs` extension, rather than the CommonJS this started as. Every
 * bundler in the repo then reads it natively: Rollup, which Storybook builds with,
 * treats a source `.js` as ESM and so found no named exports on a `module.exports`
 * object at all, failing the build while Next and Vitest interoperated happily.
 * `sitemapper.js` reaches it through a dynamic `import()` rather than `require()`,
 * because `require()` of ESM is only unflagged from Node 22.12 and this repo's
 * `engines` field allows any 22.x.
 *
 * Keep it that way. A single `import` of anything else, or one line of TypeScript
 * syntax, and the deploy side has to grow its own copy again.
 *
 * ## What this rule does *not* cover
 *
 * NFL game pages that cross-canonical to an evergreen `/matchups/nfl/...` page.
 * `seoMetadata.ts` forces those indexable, so their accumulated signal reaches the
 * canonical target rather than a `noindex` crossing the canonical pair, and the
 * sitemap leaves every one of them out whatever this rule says, because a page whose
 * canonical is elsewhere is not its own to submit. Both decisions come from
 * `nflGameMatchupCanonical.mjs`, the same resolver on both sides;
 * `marketPageIndexation.vitest.ts` pins that they agree.
 */

/**
 * Series whose pages are no-indexed outright, lowercase.
 *
 * Determined one-off markets from Kalshi's early years — resolved COVID case
 * counts, 2021 Fed nominations, dead weather series. They rank for nothing and
 * dilute the crawl budget of everything that does.
 *
 * Lifted from `TICKERS_TO_NO_INDEX` in `react_pages/Event/utils.ts`, where the
 * sitemap generator could not reach it — which is why its copy of the rule silently
 * skipped this exclusion. That file still carries the list, but nothing imports it
 * any more; deleting it is fenced off while another change lands in that tree, and
 * `tests/unit/marketPageIndexation.vitest.ts` pins the orphan to this list until
 * someone does.
 */
const NOINDEX_SERIES_TICKERS = [
    'cook',
    'michtemp',
    'rainhou',
    'peso',
    'evmkt',
    'indoorsf',
    'vaxxomni',
    'lanarey',
    'fxeuro',
    'vanratewc',
    'caoy',
    'gnsour',
    'casem',
    'booster',
    'cased',
    'vanratese',
    'casew',
    'bedoya',
    'schlsf',
    'casesurge',
    'conrova',
    'onrrp',
    'lizarraga',
    'taper',
    'vaxxkid',
    'covidbill',
    'happier',
    'varba2',
    'donda',
    'case7dny',
    'case7d',
    'caselow',
    'casewfr',
    'kanye',
    'monkeypox',
    'rainno',
    'cngdp',
    'carbontax',
    'case7dnyc',
    'casewcn',
    'case7da',
    'jefferson',
    'fedconf',
    'hurcland',
    'reclaw',
    'covpill',
    'casewde',
    'childs',
    'dinenyc',
    'schlnyc',
    'loverboy',
    'ftaper',
    'vardelta',
    'hurcath',
    'vaxx',
    'case7dca',
    'ambcn',
    'case7dcn',
    'tsa',
    'brainard',
    'dinenycs',
    'orddly',
    'trddefcn',
    'pmla',
    'newflu',
    'case7dtx',
    'case7dfl',
    'casewit',
    'fluv',
    'forb',
    'ctcs',
    'covvhc',
    'evict',
    'kxloper',
    'kxboeing737max9',
    'kxmurthymissouri',
    'kxmessiah',
    'kxsffa',
    'kxdrivers',
    'kxyinvert',
    'kxrthotds2',
    'kxpleavelaw',
    'kxopenaisuit',
    'kxmcstellarblade',
    'kxwga',
    'kxvisionpro',
    'kxeclipsecover',
    'kxvoncuk',
    'kxngasmin',
    'kxvanratemw',
    'kxgrantspass',
    'kxsdebt',
    'kxrtsympath',
    'kxvanratene',
    'kxseaice',
    'kxrtfirstomen',
    'kxlcpimin',
    'kxtravelwarn',
    'kxmaskmand',
    'kxnepa',
    'kxsotw',
    'kxdiesel',
    'kxcarvmak',
    'kxmead',
    'kxrtacolyte',
    'kxrtghostbusterfe',
    'kxfednom',
    'kxuawlength',
    'kxrecnc',
    'kxinfralw',
    'kxdaylight',
    'kxrtcivil',
    'kxmichigancoach',
    'kxportla',
    'kxtechreg',
    'kxboard',
    'kxu3min',
    'kxuaw',
    'kxolymcancel',
    'kxsag',
    'kxvaxmand',
    'kxmetacriticerdtree',
    'kxmooreus',
    'kxsaltx',
    'kxsohn',
    'kxtiktoksell',
    'kxinfra',
    'kxemmycacto',
    'kxolymed',
    'kxapplewatch',
    'kxrecnch',
    'kxgpt4p5',
    'kxopenaiboardj',
    'kxgptparam',
    'kxprek',
    'kxemmydactr',
    'kxmta',
    'kxvanrate',
    'kxdonda2',
    'kxvanratesc',
    'kxneuralink',
    'kxrrf',
    'kxavatarseason2',
    'kxrtabigail',
    'kxppunish',
    'kxoilw',
    'kxcorptax',
    'kxfdacaliff',
    'kxmchellblade2',
    'kxvaromic',
    'kxrtgodzillaxkong',
    'kxngas',
    'kxlifeexp',
    'kxrtkinds',
    'kxbillboardpeakchihiro',
    'kxrtfallout',
    'kxbillboardpeaknlg',
    'kxtopsongrecordppp',
    'kxrtmegalopolis',
    'kx1songbillieeilish',
    'kxrtinsideout2',
    'kxrtchallengers',
    'kxrtdespicableme4',
    'kxbillboardpeakhoudini',
    'kxdebate',
    'kx1songmacklemore',
    'kxrtfuriosa',
    'kxjbeardbnr',
    'kxdebatehappen',
    'kxbillboardpeakppp',
    'kxmcdawntrail',
    'kxrtwatchers',
    'kxtopsongrecord',
    'kxrtapes',
    'kxrtthebear',
    'kxlbjr',
    'kxrtif',
    'kxjbeardort',
    'kxrtezra',
    'kxrtquietplacedayone',
    'kxrtthefallguy',
    'kxtopsongrecordihsh',
    'kxbillboardpeak-e',
    'kxrtblinktwice',
    'kxrtbeetlejuice',
    'kxrtreagan',
    'kxbillboardpeaktaste',
    'kxmcmetaphor',
    'kxrtspeaknoevil',
    'kxrtagathaallalong',
    'kxhome',
    'kxmcblackops6',
    'kxmcsilenthill2',
    'kxrtchristmaspageant',
    'kxrtsmile2',
    'kxrtterrifier3',
    'kxrtvenom3',
    'kxrtwildrobot',
    'kxcook',
    'kxmichtemp',
    'kxrainhou',
    'kxpeso',
    'kxevmkt',
    'kxindoorsf',
    'kxvaxxomni',
    'kxlanarey',
    'kxfxeuro',
    'kxvanratewc',
    'kxcaoy',
    'kxgnsour',
    'kxcasem',
    'kxbooster',
    'kxcased',
    'kxvanratese',
    'kxcasew',
    'kxbedoya',
    'kxschlsf',
    'kxcasesurge',
    'kxconrova',
    'kxonrrp',
    'kxlizarraga',
    'kxtaper',
    'kxvaxxkid',
    'kxcovidbill',
    'kxhappier',
    'kxvarba2',
    'kxdonda',
    'kxcase7dny',
    'kxcase7d',
    'kxcaselow',
    'kxcasewfr',
    'kxkanye',
    'kxmonkeypox',
    'kxrainno',
    'kxcngdp',
    'kxcarbontax',
    'kxcase7dnyc',
    'kxcasewcn',
    'kxcase7da',
    'kxjefferson',
    'kxfedconf',
    'kxhurcland',
    'kxreclaw',
    'kxcovpill',
    'kxcasewde',
    'kxchilds',
    'kxdinenyc',
    'kxschlnyc',
    'kxloverboy',
    'kxftaper',
    'kxvardelta',
    'kxhurcath',
    'kxvaxx',
    'kxcase7dca',
    'kxambcn',
    'kxcase7dcn',
    'kxtsa',
    'kxbrainard',
    'kxdinenycs',
    'kxdorddly',
    'kxtrddefcn',
    'kxpmla',
    'kxnewflu',
    'kxcase7dtx',
    'kxcase7dfl',
    'kxcasewit',
    'kxfluv',
    'kxforb',
    'kxctcs',
    'kxcovvhc',
    'kxevict',
];

const NOINDEX_SERIES_TICKER_SET = new Set(NOINDEX_SERIES_TICKERS);

/** Whether a series ticker is one of the determined series that is never indexed. */
const isDeterminedSeriesToNoIndex = (seriesTicker) =>
    !!seriesTicker && NOINDEX_SERIES_TICKER_SET.has(String(seriesTicker).toLowerCase());

/** A market in one of these statuses is no longer trading. */
const MARKET_CLOSED_STATUSES = ['closed', 'determined', 'settled', 'finalized'];

/** Series that relist often enough that yesterday's page is worthless to a searcher. */
const HIGH_FREQUENCY_SERIES = ['daily', 'weekly', 'hourly', 'fifteen_min'];

/** The subset that relists so often it is never worth indexing, open or closed. */
const SUPER_HIGH_FREQUENCY_SERIES = ['hourly', 'fifteen_min'];

/** Event volume, in contracts, above which a settled page keeps its page. */
const HIGH_VOLUME_EVENT_THRESHOLD = 5_000_000;

/**
 * The date the closed-market policy changed: from this point on, a settled page is
 * kept regardless of volume. Older settled pages still have to clear the volume bar.
 */
const CLOSED_MARKET_POLICY_LIMIT_DATE = new Date('2025-12-22T00:00:00Z');

/** Competition scopes that keep a sports page indexable; everything else is a secondary market. */
const PRIMARY_COMPETITION_SCOPES = ['Game', 'Futures', 'Awards'];

/**
 * A market's lifetime volume in contracts, one side of each trade: `volume_contracts_fp`
 * when the exchange sends it, else the legacy one-sided integer `dollar_volume`.
 */
const marketVolumeContracts = (market) => {
    const fixedPoint = market && market.volume_contracts_fp;
    if (typeof fixedPoint === 'string' && fixedPoint !== '') {
        const parsed = Number.parseFloat(fixedPoint);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return (market && market.dollar_volume) || 0;
};

/**
 * Whether a `/markets/...` event page should render `noindex` — and, equivalently,
 * be left out of `sitemap-markets.xml`.
 *
 * `isIndexableEnv` is an explicit input rather than a read of
 * `NEXT_PUBLIC_BUILD_SITEMAP` here, for two reasons: this file must stay free of
 * `process.env` so Next's build-time inlining of `NEXT_PUBLIC_*` keeps happening in
 * the app module that owns the read (`utils/markets.ts`), and the sitemap generator
 * has no use for the gate at all — it only ever runs to produce kalshi.com's own
 * sitemap, so it passes `true`. The gate exists to stop preview and staging deploys
 * rendering indexable pages, which is a property of the environment, not of the event.
 */
const shouldNoIndexMarketPage = (eventVo, series, options) => {
    const isIndexableEnv = !!(options && options.isIndexableEnv);

    if (!isIndexableEnv) return true;

    if (isDeterminedSeriesToNoIndex(eventVo && eventVo.series_ticker)) return true;

    const frequency = (series && series.frequency) || '';
    if (SUPER_HIGH_FREQUENCY_SERIES.includes(frequency)) return true;

    const markets = (eventVo && eventVo.markets) || [];
    const isClosed = markets.every((market) => MARKET_CLOSED_STATUSES.includes(market && market.status));
    if (!isClosed) return false;

    const isHighFrequencySeries = HIGH_FREQUENCY_SERIES.includes(frequency);

    const eventVolume = markets.reduce((total, market) => total + marketVolumeContracts(market), 0);
    const isHighVolumeEvent = eventVolume >= HIGH_VOLUME_EVENT_THRESHOLD;

    const competitionScope =
        (eventVo && eventVo.product_metadata && eventVo.product_metadata.competition_scope) || undefined;
    const isSportsSecondaryMarket = !!competitionScope && !PRIMARY_COMPETITION_SCOPES.includes(competitionScope);

    const closedBeforeLimitDate = markets.every(
        (market) => new Date(market && market.close_date) < CLOSED_MARKET_POLICY_LIMIT_DATE,
    );

    // Two buckets for settled pages:
    //   1. closed before the limit date — no-indexed if high frequency, a sports
    //      secondary market, or low volume;
    //   2. closed after it — no-indexed only if high frequency or a sports secondary
    //      market, whatever the volume.
    return closedBeforeLimitDate
        ? isHighFrequencySeries || isSportsSecondaryMarket || !isHighVolumeEvent
        : isHighFrequencySeries || isSportsSecondaryMarket;
};

export { NOINDEX_SERIES_TICKERS, isDeterminedSeriesToNoIndex, shouldNoIndexMarketPage };
